<script lang="ts">
  import { onMount } from "svelte";
  import { auth } from "../stores/auth";
  import { api } from "../lib/api";

  const STORAGE_KEY = "antares_token";

  let mode = $state<"password" | "token">("password");
  let token = $state("");
  let username = $state("");
  let password = $state("");
  let remember = $state(true);
  let errorMsg = $state("");
  let submitting = $state(false);

  onMount(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      token = saved;
      remember = true;
    }
  });

  async function handleLogin() {
    errorMsg = "";
    submitting = true;
    try {
      let res;
      if (mode === "token") {
        res = await api.loginWithToken(token);
        if (res.authenticated && remember) {
          localStorage.setItem(STORAGE_KEY, token);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } else {
        res = await api.loginWithPassword(username, password);
      }

      if (res.authenticated) {
        auth.set({ authenticated: true, user: res.user ?? null, authType: res.authType ?? null });
        window.location.hash = "/";
      } else {
        errorMsg = "Authentication failed";
      }
    } catch (err: any) {
      errorMsg = err.message ?? "Login failed";
    } finally {
      submitting = false;
    }
  }
</script>

<div class="login-page">
  <div class="login-card">
    <h1>Antares</h1>
    <p class="subtitle">Kubernetes Workload Console</p>

    <div class="login-tabs">
      <button class:active={mode === "password"} onclick={() => mode = "password"}>Password</button>
      <button class:active={mode === "token"} onclick={() => mode = "token"}>Token</button>
    </div>

    <form onsubmit={(e) => { e.preventDefault(); handleLogin(); }}>
      {#if mode === "password"}
        <label for="username">Username</label>
        <input
          id="username"
          type="text"
          bind:value={username}
          placeholder="Username"
          disabled={submitting}
          autocomplete="username"
        />
        <label for="password">Password</label>
        <input
          id="password"
          type="password"
          bind:value={password}
          placeholder="Password"
          disabled={submitting}
          autocomplete="current-password"
        />
      {:else}
        <label for="token">ServiceAccount Token</label>
        <textarea
          id="token"
          bind:value={token}
          placeholder="Paste your kubectl token here..."
          rows={4}
          disabled={submitting}
        ></textarea>

        <label class="remember">
          <input type="checkbox" bind:checked={remember} />
          Remember token in this browser
        </label>
      {/if}

      {#if errorMsg}
        <div class="error">{errorMsg}</div>
      {/if}

      <button
        type="submit"
        class="btn-primary"
        disabled={(mode === "token" ? !token.trim() : !username.trim() || !password) || submitting}
      >
        {submitting ? "Authenticating..." : "Login"}
      </button>
    </form>

    {#if mode === "token"}
      <div class="hint">
        <p>For a non-expiring token:</p>
        <code>kubectl get secret antares-user-token -n antares -o jsonpath='&#123;.data.token&#125;' | base64 -d</code>
      </div>
    {/if}
  </div>
</div>

<style>
  .login-page {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: calc(100vh - 48px - 3rem);
  }

  .login-card {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 2rem;
    width: 100%;
    max-width: 440px;
  }

  h1 {
    color: var(--accent);
    font-size: 1.5rem;
    margin-bottom: 0.25rem;
  }

  .subtitle {
    color: var(--text-muted);
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }

  .login-tabs {
    display: flex;
    gap: 0;
    border-bottom: 1px solid var(--border);
    margin-bottom: 1.25rem;
  }

  .login-tabs button {
    flex: 1;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    color: var(--text-muted);
    padding: 0.5rem;
    cursor: pointer;
    font-size: 0.85rem;
  }

  .login-tabs button:hover { color: var(--text); }
  .login-tabs button.active { color: var(--accent); border-bottom-color: var(--accent); }

  label {
    display: block;
    font-size: 0.85rem;
    color: var(--text-muted);
    margin-bottom: 0.5rem;
  }

  input[type="text"], input[type="password"] {
    width: 100%;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 4px;
    color: var(--text);
    padding: 0.6rem 0.75rem;
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
  }

  input[type="text"]:focus, input[type="password"]:focus {
    outline: none;
    border-color: var(--accent);
  }

  textarea {
    width: 100%;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 4px;
    color: var(--text);
    padding: 0.75rem;
    font-family: var(--font-mono);
    font-size: 0.8rem;
    resize: vertical;
    margin-bottom: 0.75rem;
  }

  textarea:focus {
    outline: none;
    border-color: var(--accent);
  }

  .remember {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8rem;
    color: var(--text-muted);
    cursor: pointer;
    margin-bottom: 1rem;
    user-select: none;
  }

  .remember input { cursor: pointer; }

  .error {
    color: var(--danger);
    font-size: 0.85rem;
    margin-bottom: 1rem;
  }

  .btn-primary {
    width: 100%;
    padding: 0.6rem;
    background: var(--accent);
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 0.9rem;
    cursor: pointer;
  }

  .btn-primary:hover:not(:disabled) { background: var(--accent-hover); }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

  .hint {
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid var(--border);
    text-align: center;
  }

  .hint p {
    font-size: 0.75rem;
    color: var(--text-muted);
    margin-bottom: 0.5rem;
  }

  .hint code {
    font-size: 0.7rem;
    color: var(--text-muted);
    font-family: var(--font-mono);
    word-break: break-all;
  }
</style>
